import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useProvisionSsl, useGetDomain } from "../../hooks/useDomain";
import styles from "./Provisioning.module.css";
import logo from "../../assets/logo.png";

type StepStatus = "completed" | "active" | "pending" | "failed";

interface Step { id: number; label: string; description: string; status: StepStatus; }

function StepIcon({ status }: { status: StepStatus }) {
  if (status === "completed") return (<div className={`${styles.icon} ${styles.iconDone}`}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20,6 9,17 4,12" /></svg></div>);
  if (status === "active") return (<div className={styles.iconActiveWrapper}><div className={styles.iconActiveRing} /><div className={styles.iconActivePulse} /></div>);
  if (status === "failed") return (<div className={`${styles.icon} ${styles.iconDone}`} style={{ background: "#DC2626" }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></div>);
  return <div className={`${styles.icon} ${styles.iconPending}`} />;
}

const Provisioning = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { execute: provisionSsl } = useProvisionSsl();
  const { execute: getDomain } = useGetDomain();
  const mounted = useRef(true);

  const locState = location.state as { domain?: string; domainId?: string } | null;
  const domain = locState?.domain || "yourdomain.com";
  const domainId = locState?.domainId || "";

  const [steps, setSteps] = useState<Step[]>([
    { id: 1, label: "DNS Verified", description: "Domain is pointing to our server", status: "completed" },
    { id: 2, label: "Configuring server", description: "Creating Nginx virtual host config", status: "completed" },
    { id: 3, label: "Installing SSL", description: "Issuing Let's Encrypt certificate", status: "active" },
    { id: 4, label: "Enabling HTTPS", description: "Forcing HTTPS & setting auto-renew", status: "pending" },
  ]);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => { return () => { mounted.current = false; }; }, []);

  useEffect(() => {
    if (!domainId) return;

    const runProvisioning = async () => {
      const result = await provisionSsl(domainId);

      if (!mounted.current) return;

      if (result && result.status === "active") {
        setSteps((s) => s.map((step) => ({ ...step, status: "completed" as StepStatus })));
        setTimeout(() => {
          if (mounted.current) navigate("/success", { state: { domain } });
        }, 1500);
        return;
      }

      const updated = await getDomain(domainId);
      if (!mounted.current) return;

      if (updated) {
        const sslOk = updated.sslStatus === "active";
        const nginxOk = updated.nginxConfigured;

        setSteps([
          { id: 1, label: "DNS Verified", description: "Domain is pointing to our server", status: "completed" },
          { id: 2, label: "Configuring server", description: "Creating Nginx virtual host config", status: nginxOk ? "completed" : "failed" },
          { id: 3, label: "Installing SSL", description: "Issuing Let's Encrypt certificate", status: sslOk ? "completed" : updated.sslStatus === "failed" ? "failed" : "active" },
          { id: 4, label: "Enabling HTTPS", description: "Forcing HTTPS & setting auto-renew", status: updated.status === "active" ? "completed" : "pending" },
        ]);

        if (updated.sslStatus === "failed" || !nginxOk) {
          setHasFailed(true);
        }
      } else {
        setSteps((s) => s.map((step) => step.id === 3 ? { ...step, status: "failed" as StepStatus } : step));
        setHasFailed(true);
      }
    };

    runProvisioning();
  }, [domainId, provisionSsl, getDomain, navigate, domain]);

  const completedCount = steps.filter((s) => s.status === "completed").length;
  const progressPct = (completedCount / steps.length) * 100;

  const handleRetry = async () => {
    setHasFailed(false);
    setSteps((s) => s.map((step) => step.status === "failed" ? { ...step, status: "active" as StepStatus } : step));
    const result = await provisionSsl(domainId);
    if (result && result.status === "active") {
      setSteps((s) => s.map((step) => ({ ...step, status: "completed" as StepStatus })));
      setTimeout(() => navigate("/success", { state: { domain } }), 1500);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.blobBlue} />
      <div className={styles.blobOrange} />
      <div className={styles.container}>
        <div className={styles.logo}><img src={logo} alt="InstantGDS" className={styles.logoImg} /></div>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.stepBadge}>Step 3 of 3</div>
            <h1 className={styles.title}>{hasFailed ? "Provisioning Issue" : "Setting up your website"}</h1>
            <p className={styles.subtitle}>Provisioning <span className={styles.domainHighlight}>{domain}</span></p>
          </div>
          <div className={styles.overallProgress}>
            <div className={styles.overallProgressTrack}><div className={styles.overallProgressBar} style={{ width: `${progressPct}%` }} /></div>
            <span className={styles.overallProgressLabel}>{completedCount}/{steps.length} steps</span>
          </div>
          <div className={styles.steps}>
            {steps.map((step, index) => (
              <div key={step.id} className={styles.stepRow}>
                <div className={styles.stepLeft}>
                  <StepIcon status={step.status} />
                  {index < steps.length - 1 && (<div className={`${styles.connector} ${step.status === "completed" ? styles.connectorDone : ""}`} />)}
                </div>
                <div className={`${styles.stepContent} ${step.status === "pending" ? styles.stepContentPending : ""}`}>
                  <div className={styles.stepLabelRow}>
                    <span className={`${styles.stepLabel} ${styles[`stepLabel_${step.status}`]}`}>{step.label}</span>
                    {step.status === "active" && <span className={styles.activePill}>In progress</span>}
                    {step.status === "completed" && <span className={styles.donePill}>Done</span>}
                    {step.status === "failed" && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "#FEF2F2", color: "#DC2626", fontWeight: 600 }}>Failed</span>}
                  </div>
                  <span className={styles.stepDesc}>{step.description}</span>
                </div>
              </div>
            ))}
          </div>
          {hasFailed && (
            <button onClick={handleRetry} style={{ marginTop: 16, width: "100%", padding: "12px", background: "#1D4ED8", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14 }}>Retry Provisioning</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Provisioning;